import { Big } from 'big.js';
import TwtJob from '~/tenants/twt/model/TwtJob';
import { TwtPerformableConfig } from '~/tenants/twt/model/TwtOrder';
import range from 'lodash.range';

interface TwtSimpleJobConfig {
  revenueMinimum?: number;
  revenuePer1000?: number;
  revenueFirstHour?: number;
  expenseMinimum?: number;
  expensePer1000?: number;
  onsiteMinimum: number;
  onsitePer1000: number;
}

/**
 * Provider config key families that carry per-provider on-site minute
 * bands by square footage (`<prefix>_0_1000` ... `<prefix>_5001_6000`,
 * `<prefix>_every_1000_after_6000` — see TwtProviderStaticData). Each
 * job type reads its OWN family so a provider's Matterport and Nucleus
 * timings can differ.
 */
export type TwtOnsiteBandPrefix = 'scan_onsite' | 'nucleus_onsite';

export abstract class TwtSimpleJob<P extends TwtPerformableConfig> extends TwtJob<P> {
  protected abstract get config(): TwtSimpleJobConfig;

  protected expensePer(_thousands: number): number | undefined {
    return undefined;
  }

  protected expenseMinimum(): number | undefined {
    return undefined;
  }

  protected revenueMinimum(): number | undefined {
    return undefined;
  }

  protected revenuePer(_thousands: number): number | undefined {
    return undefined;
  }

  protected get revenueFirstHour(): Big {
    return new Big(this.config.revenueFirstHour ?? 0);
  }

  protected get expenseFirstHour(): Big | null {
    return null;
  }

  protected get revenuePerMinute(): Big | null {
    return null;
  }

  protected get expensePerMinute(): Big | null {
    return null;
  }

  get minutes() {
    return 0;
  }

  onsite(): number {
    if(this.order.metadata.transfer){
      return 0;
    }

    return this.baseOnsite();
  }

  /** The tenant-wide formula: `onsiteMinimum + floor((sqft - 1) / 1000) * onsitePer1000`. */
  protected baseOnsite(): number {
    return this.config.onsiteMinimum + this.sqftMulitpler() * this.config.onsitePer1000;
  }

  /**
   * On-site minutes with the assigned provider's square-footage bands
   * overlaid on the base formula. Shared by every job type that carries
   * a band family so all of them have IDENTICAL semantics:
   *
   *   - transfer orders are 0 (same as onsite())
   *   - no provider: the base formula
   *   - sqft <= 6,000: the band containing the sqft, falling back to the
   *     base formula PER BAND when that band is unset on the provider
   *   - sqft > 6,000: `base + per1000 * ceil((sqft - 6000) / 1000)` where
   *     base is the provider's 5001_6000 band (falling back to the job's
   *     onsiteMinimum) and per1000 is the provider's every_1000_after_6000
   *     increment (falling back to the job's onsitePer1000)
   *
   * Extracted verbatim from TwtMatterportJob.onsite() so Matterport is
   * unchanged; Nucleus reads its own `nucleus_onsite_*` family.
   */
  protected onsiteWithProviderBands(prefix: TwtOnsiteBandPrefix): number {
    if (this.order.metadata.transfer) {
      return 0;
    }

    const onsite = this.baseOnsite();

    if (!this.provider) {
      return onsite;
    }

    const bands = this.provider.config;
    const sqft = this.sqft;

    if (sqft <= 1000) {
      return bands[`${prefix}_0_1000`] ?? onsite;
    } else if (sqft <= 2000) {
      return bands[`${prefix}_1001_2000`] ?? onsite;
    } else if (sqft <= 3000) {
      return bands[`${prefix}_2001_3000`] ?? onsite;
    } else if (sqft <= 4000) {
      return bands[`${prefix}_3001_4000`] ?? onsite;
    } else if (sqft <= 5000) {
      return bands[`${prefix}_4001_5000`] ?? onsite;
    } else if (sqft <= 6000) {
      return bands[`${prefix}_5001_6000`] ?? onsite;
    }

    const base = bands[`${prefix}_5001_6000`] ?? this.config.onsiteMinimum;
    const per1000 = bands[`${prefix}_every_1000_after_6000`] ?? this.config.onsitePer1000;

    return base + per1000 * Math.ceil((sqft - 6000) / 1000);
  }

  protected get extraMinutes() {
    return this.minutes - 60;
  }

  protected simpleExpense() {
    if (this.hourly) {
      let amount = this.expenseFirstHour ?? new Big(0);

      if (this.extraMinutes > 0 && this.expensePerMinute) {
        amount = amount.plus(this.expensePerMinute.times(this.extraMinutes));
      }

      return amount;
    }

    let expense = new Big(this.expenseMinimum() ?? this.config.expenseMinimum ?? 0);

    for (const thousand of range(0, this.sqftMulitpler())) {
      expense = expense.add(new Big(this.expensePer(thousand) ?? this.config.expensePer1000 ?? 0));
    }

    return expense;
  }

  protected simpleRevenue() {
    if (this.hourly) {
      let amount = this.revenueFirstHour;

      if (this.extraMinutes > 0 && this.revenuePerMinute) {
        amount = amount.plus(this.revenuePerMinute.times(this.extraMinutes));
      }

      return {
        amount,
        type: 'hourly' as const,
      };
    }

    let revenue = new Big(this.revenueMinimum() ?? this.config.revenueMinimum ?? 0);

    for (const thousand of range(0, this.sqftMulitpler())) {
      revenue = revenue.add(new Big(this.revenuePer(thousand) ?? this.config.revenuePer1000 ?? 0));
    }

    return {
      amount: revenue,
      type: 'sqft' as const,
    };
  }

  protected sqftMulitpler(_revenue = false) {
    if (this.sqft === 0) {
      return 0;
    }

    return Math.floor((this.sqft - 1) / 1000);
  }
}
